import { Button, Radio, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSubscriptionByIdQuery } from "src/api/services/patientSubscriptionApi";
import { useCreateVisitMutation } from "src/api/services/visitApi";
import LoadingButton from "src/components/Buttons/LoadingButton";
import Loading from "src/components/Loading/Loading";
import { MatchParam } from "src/constants/matches";
import { Paths } from "src/constants/paths";
import { useIsMobile } from "src/hooks/useIsMobile";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import WhiteCard from "src/shared/components/WhiteCard";

const CancelSubscription = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const page = "subscriptions.cancel";
  const { subscriptionId } = useParams<MatchParam<"SUBSCRIPTION_ID">>();
  const { data, isLoading } = useGetSubscriptionByIdQuery(
    Number(subscriptionId)
  );
  const [createVisit] = useCreateVisitMutation();
  const locale = usePatientLocale();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const subscription = useMemo(() => {
    if (data == null) return null;

    return data;
  }, [data]);

  const renewalDate = useMemo(() => {
    if (subscription?.current_period_end == null) return "";

    const dateFormat =
      locale === enUS ? "MMMM d, yyyy" : "d 'de' MMMM 'de' yyyy";

    return format(new Date(subscription.current_period_end), dateFormat, {
      locale,
    });
  }, [locale, subscription?.current_period_end]);

  const startCancellation = useCallback(async () => {
    if (subscription == null) return null;
    setLoading(true);

    try {
      const { data: visit } = await createVisit({
        product: subscription.product.slug,
        visit_type: "Cancellation",
      });

      if (visit != null) {
        navigate(
          `/${subscription.product.slug}/${visit.slug}/${subscriptionId}/${Paths.CANCELLATION}/${visit.intake.first_question}`
        );
      }
    } catch (err) {
      console.error("Unable to start cancellation visit", err);
    } finally {
      setLoading(false);
    }
  }, [createVisit, navigate, subscription, subscriptionId]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Stack width="100%" alignItems={"center"} gap={3}>
      <Stack gap={2} textAlign={"center"}>
        <Typography variant="h5" fontWeight={700}>
          {t(`${page}.header`, "Your subscription will auto-renew on:")}
        </Typography>
        <Typography variant="h6">{renewalDate}</Typography>
      </Stack>
      <WhiteCard direction={"row"}>
        <Stack gap={2}>
          <Typography>{t(`${page}.text`, "Cancel subscription")}</Typography>
          <Typography>
            {t(
              `${page}.disclaimer`,
              "You’ll have access to your subscription until {{renewalDate}}",
              { renewalDate }
            )}
          </Typography>
        </Stack>
        <Radio checked={true} disableRipple={true} />
      </WhiteCard>
      <Stack direction="row" gap={2} width={isMobile ? "100%" : "55%"}>
        <Button fullWidth variant="outlined" onClick={() => navigate(-1)}>
          {t(`${page}.button.nevermind`, "Nevermind")}
        </Button>
        <LoadingButton loading={loading} fullWidth onClick={startCancellation}>
          {t(`signup.submit`)}
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

export default CancelSubscription;
